// Skin-weight tools modelled on Blender's Weights menu (Normalize All, Smooth,
// Mirror, Transfer, Limit Total, Levels, Clean, Gradient, Locks). Every tool is
// a pure function over one mesh's per-vertex influence lists and returns new
// arrays. All results pass through `finalize`, which guarantees finite,
// positive weights, at most four influences per vertex and a sum of 1, while
// leaving locked joints exactly as they were.
import {
  cloneRig,
  type Influence,
  type RigDoc,
  type RigFrame,
  type RigGeometry,
  type Vec3,
} from '../rig-doc.js';

export type VertexWeights = Influence[][];
export type Locks = ReadonlySet<string>;
export type BlendMode = 'replace' | 'add' | 'subtract';
export interface LockOptions {
  locked?: Locks;
}
export interface Adjacency {
  /** Representative vertex of each vertex's weld group (vertices sharing a position). */
  weld: number[];
  /** Neighbouring representatives of each vertex (shared by its weld group). */
  neighbors: number[][];
}
export interface FinalizeOptions extends LockOptions {
  /** Unlocked joints whose new value is kept; the other unlocked joints fill the rest. */
  pinned?: ReadonlySet<string>;
  limit?: number;
  /** Allow the result to have no influences (otherwise the original is kept). */
  allowEmpty?: boolean;
}

export const MAX_INFLUENCES = 4;
const EPSILON = 1e-6;

const copy = (v: readonly Influence[]): Influence[] => v.map((i) => ({ ...i }));
const total = (entries: [string, number][]) => entries.reduce((s, e) => s + e[1], 0);
const clamp01 = (v: number) => Math.max(0, Math.min(1, v));
const byWeight = (a: [string, number], b: [string, number]) =>
  b[1] - a[1] || (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0);
function merge(v: readonly Influence[]): Map<string, number> {
  const result = new Map<string, number>();
  for (const i of v)
    if (i.joint && Number.isFinite(i.weight) && i.weight > EPSILON)
      result.set(i.joint, (result.get(i.joint) ?? 0) + i.weight);
  return result;
}
const weightOf = (v: readonly Influence[], joint: string) =>
  v.reduce((s, i) => (i.joint === joint && Number.isFinite(i.weight) ? s + i.weight : s), 0);

/**
 * Turns an op's `candidate` for a vertex into valid weights. Duplicate joints
 * are merged, zero/invalid weights dropped and the strongest `limit` kept
 * (locked first, then pinned). Locked joints keep the `original` weight; a
 * vertex whose influences are all locked is returned unchanged. Unlocked
 * weights are scaled to fill `1 - lockedSum`, holding pinned joints where
 * possible.
 */
export function finalize(
  original: readonly Influence[],
  candidate: readonly Influence[],
  { locked, pinned, limit = MAX_INFLUENCES, allowEmpty = false }: FinalizeOptions = {},
): Influence[] {
  const max = Math.max(1, Math.min(MAX_INFLUENCES, Math.floor(limit) || 1));
  const before = merge(original);
  const lockedHere = [...before].filter(([j]) => locked?.has(j));
  if (before.size && lockedHere.length === before.size) return copy(original);
  const next = merge(candidate);
  for (const joint of [...next.keys()]) if (locked?.has(joint)) next.delete(joint);
  const keptLocked = lockedHere.sort(byWeight).slice(0, max);
  const free = [...next]
    .sort((a, b) => Number(!!pinned?.has(b[0])) - Number(!!pinned?.has(a[0])) || byWeight(a, b))
    .slice(0, max - keptLocked.length);
  const pin = free.filter(([j]) => pinned?.has(j)),
    rest = free.filter(([j]) => !pinned?.has(j));
  const remaining = Math.max(0, 1 - total(keptLocked)),
    p = total(pin),
    o = total(rest);
  // Divide before multiplying so already-normalized weights round-trip exactly.
  let pinFill = p,
    restFill = 0;
  if (o > 0 && p < remaining) restFill = remaining - p;
  else if (p > 0) pinFill = remaining;
  const result = [
    ...keptLocked,
    ...pin.map(([j, w]): [string, number] => [j, (w / p) * pinFill]),
    ...rest.map(([j, w]): [string, number] => [j, (w / o) * restFill]),
  ]
    .filter(([, w]) => Number.isFinite(w) && w > 0)
    .sort(byWeight)
    .map(([joint, weight]) => ({ joint, weight }));
  if (!result.length && !allowEmpty && original.length)
    return finalize(original, original, { locked, limit: max, allowEmpty: true });
  return result;
}

/** Normalize All: each vertex sums to 1; locked joints keep their weight. */
export function normalizeAll(w: VertexWeights, { locked }: LockOptions = {}): VertexWeights {
  return w.map((v) => finalize(v, v, { locked }));
}

/**
 * Builds mesh neighbourhoods from triangle indices. With `positions`, vertices
 * at the same position (split along UV/normal seams) are welded so tools treat
 * them as one vertex.
 */
export function buildAdjacency(
  indices: readonly number[],
  vertexCount: number,
  positions?: readonly Vec3[],
): Adjacency {
  const weld = Array.from({ length: vertexCount }, (_, i) => i);
  if (positions) {
    const seen = new Map<string, number>();
    for (let i = 0; i < vertexCount; i++) {
      const p = positions[i];
      if (!p) continue;
      const key = p.map((c) => Math.round(c * 1e6)).join(',');
      const rep = seen.get(key);
      if (rep === undefined) seen.set(key, i);
      else weld[i] = rep;
    }
  }
  const sets = Array.from({ length: vertexCount }, () => new Set<number>());
  for (let t = 0; t + 2 < indices.length; t += 3) {
    const tri = [indices[t]!, indices[t + 1]!, indices[t + 2]!].map((i) => weld[i] ?? -1);
    for (const a of tri) for (const b of tri) if (a !== b && a >= 0 && b >= 0) sets[a]!.add(b);
  }
  const lists = sets.map((s) => [...s].sort((a, b) => a - b));
  return { weld, neighbors: weld.map((rep) => lists[rep] ?? []) };
}

export interface SmoothOptions extends LockOptions {
  factor?: number;
  iterations?: number;
  /** Only smooth these joints (others are rescaled to fill); default all. */
  joints?: ReadonlySet<string>;
  selection?: ReadonlySet<number>;
}
/** Smooth: Laplacian blend of each vertex towards its mesh neighbours. */
export function smooth(
  w: VertexWeights,
  adjacency: Adjacency,
  { factor = 0.5, iterations = 1, joints, locked, selection }: SmoothOptions = {},
): VertexWeights {
  const f = clamp01(factor);
  const groups = new Map<number, number[]>();
  adjacency.weld.forEach((rep, v) => {
    if (v < w.length) groups.set(rep, [...(groups.get(rep) ?? []), v]);
  });
  // Selecting one side of a seam selects the whole weld group so seams stay closed.
  const active = [...groups.keys()].filter(
    (rep) => !selection || groups.get(rep)!.some((v) => selection.has(v)),
  );
  let current = w.map(copy);
  for (let it = 0; it < Math.max(0, Math.floor(iterations)); it++) {
    const average = new Map<number, Map<string, number>>();
    const groupWeights = (rep: number) => {
      let result = average.get(rep);
      if (!result) {
        const members = groups.get(rep) ?? [];
        result = new Map();
        for (const v of members)
          for (const [j, x] of merge(current[v] ?? []))
            result.set(j, (result.get(j) ?? 0) + x / members.length);
        average.set(rep, result);
      }
      return result;
    };
    const next = current.map(copy);
    for (const rep of active) {
      const base = groupWeights(rep),
        neighbors = (adjacency.neighbors[rep] ?? []).filter((n) => groups.has(n));
      const candidate = new Map(base);
      if (neighbors.length) {
        const mean = new Map<string, number>();
        for (const n of neighbors)
          for (const [j, x] of groupWeights(n))
            mean.set(j, (mean.get(j) ?? 0) + x / neighbors.length);
        for (const j of new Set([...base.keys(), ...mean.keys()]))
          if (!joints || joints.has(j))
            candidate.set(j, (base.get(j) ?? 0) * (1 - f) + (mean.get(j) ?? 0) * f);
      }
      const list = [...candidate].map(([joint, weight]) => ({ joint, weight }));
      for (const v of groups.get(rep)!)
        next[v] = finalize(current[v] ?? [], list, { locked, pinned: joints });
    }
    current = next;
  }
  return current;
}

const SIDE: Record<string, string> = { L: 'R', R: 'L', l: 'r', r: 'l' };
const WORDS: [string, string][] = [
  ['Left', 'Right'],
  ['left', 'right'],
  ['LEFT', 'RIGHT'],
];
/**
 * Flips a left/right joint name like Blender: `.L`/`_L`/`-L`/` L` suffixes
 * (optionally followed by `.001`), `L_`/`L.` prefixes, and Left/Right words at
 * the start or end. Names without a side are returned unchanged.
 */
export function mirrorName(name: string): string {
  const suffix = /^(.*[._\- ])([LRlr])(\.\d+)?$/.exec(name);
  if (suffix) return `${suffix[1]}${SIDE[suffix[2]!]}${suffix[3] ?? ''}`;
  const prefix = /^([LRlr])([._\- ].*)$/.exec(name);
  if (prefix) return `${SIDE[prefix[1]!]}${prefix[2]}`;
  for (const [a, b] of WORDS)
    for (const [from, to] of [
      [a, b],
      [b, a],
    ] as const) {
      if (name.startsWith(from)) return to + name.slice(from.length);
      const end = new RegExp(`${from}(\\.\\d+)?$`).exec(name);
      if (end) return name.slice(0, end.index) + to + name.slice(end.index + from.length);
    }
  return name;
}

// ---------------------------------------------------------------------------
// Spatial hash grid used by mirror and transfer.

interface Grid {
  cell: number;
  cells: Map<string, number[]>;
  min: Vec3;
  max: Vec3;
}
const cellOf = (p: Vec3, cell: number): Vec3 => [
  Math.floor(p[0] / cell),
  Math.floor(p[1] / cell),
  Math.floor(p[2] / cell),
];
function makeGrid(boxes: [Vec3, Vec3][]): Grid {
  const lo: Vec3 = [Infinity, Infinity, Infinity],
    hi: Vec3 = [-Infinity, -Infinity, -Infinity];
  for (const [a, b] of boxes)
    for (let k = 0; k < 3; k++) {
      lo[k] = Math.min(lo[k]!, a[k]!);
      hi[k] = Math.max(hi[k]!, b[k]!);
    }
  const extent = boxes.length ? Math.hypot(hi[0] - lo[0], hi[1] - lo[1], hi[2] - lo[2]) : 0;
  const cell = Math.max(extent / Math.max(1, Math.cbrt(boxes.length)), 1e-6);
  const grid: Grid = { cell, cells: new Map(), min: [0, 0, 0], max: [-1, -1, -1] };
  if (!boxes.length) return grid;
  grid.min = cellOf(lo, cell);
  grid.max = cellOf(hi, cell);
  boxes.forEach(([a, b], index) => {
    const c0 = cellOf(a, cell),
      c1 = cellOf(b, cell);
    for (let x = c0[0]; x <= c1[0]; x++)
      for (let y = c0[1]; y <= c1[1]; y++)
        for (let z = c0[2]; z <= c1[2]; z++) {
          const key = `${x},${y},${z}`,
            list = grid.cells.get(key);
          if (list) list.push(index);
          else grid.cells.set(key, [index]);
        }
  });
  return grid;
}
/** Ring search outward from `p`; `distance` returns the exact distance to an item. */
function nearest(
  grid: Grid,
  p: Vec3,
  distance: (index: number) => number,
  maxDistance = Infinity,
): { index: number; distance: number } | null {
  if (!grid.cells.size) return null;
  const c = cellOf(p, grid.cell);
  const reach = Math.max(
    ...[0, 1, 2].map((k) =>
      Math.max(Math.abs(c[k]! - grid.min[k]!), Math.abs(c[k]! - grid.max[k]!)),
    ),
  );
  let best = -1,
    bestDistance = Infinity;
  for (let r = 0; r <= reach; r++) {
    const lo = [0, 1, 2].map((k) => Math.max(c[k]! - r, grid.min[k]!)),
      hi = [0, 1, 2].map((k) => Math.min(c[k]! + r, grid.max[k]!));
    for (let x = lo[0]!; x <= hi[0]!; x++)
      for (let y = lo[1]!; y <= hi[1]!; y++) {
        const shell = Math.abs(x - c[0]) === r || Math.abs(y - c[1]) === r;
        const zs = shell
          ? Array.from({ length: Math.max(0, hi[2]! - lo[2]! + 1) }, (_, i) => lo[2]! + i)
          : [c[2] - r, c[2] + r].filter((z) => z >= lo[2]! && z <= hi[2]!);
        for (const z of new Set(zs))
          for (const index of grid.cells.get(`${x},${y},${z}`) ?? []) {
            const d = distance(index);
            if (d < bestDistance || (d === bestDistance && index < best)) {
              best = index;
              bestDistance = d;
            }
          }
      }
    // Every item in ring r + 1 is at least r cells away from p.
    if (bestDistance <= r * grid.cell || r * grid.cell > maxDistance) break;
  }
  return best >= 0 && bestDistance <= maxDistance ? { index: best, distance: bestDistance } : null;
}
const sub = (a: Vec3, b: Vec3): Vec3 => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
const dot = (a: Vec3, b: Vec3) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
const dist = (a: Vec3, b: Vec3) => Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]);
function pointGrid(points: readonly Vec3[]): Grid {
  return makeGrid(points.map((p) => [p, p]));
}

/** Barycentric coordinates of the point on triangle abc closest to p (Ericson). */
export function closestOnTriangle(p: Vec3, a: Vec3, b: Vec3, c: Vec3): Vec3 {
  const ab = sub(b, a),
    ac = sub(c, a),
    ap = sub(p, a);
  const d1 = dot(ab, ap),
    d2 = dot(ac, ap);
  if (d1 <= 0 && d2 <= 0) return [1, 0, 0];
  const bp = sub(p, b),
    d3 = dot(ab, bp),
    d4 = dot(ac, bp);
  if (d3 >= 0 && d4 <= d3) return [0, 1, 0];
  const vc = d1 * d4 - d3 * d2;
  if (vc <= 0 && d1 >= 0 && d3 <= 0) {
    const v = d1 / (d1 - d3);
    return [1 - v, v, 0];
  }
  const cp = sub(p, c),
    d5 = dot(ab, cp),
    d6 = dot(ac, cp);
  if (d6 >= 0 && d5 <= d6) return [0, 0, 1];
  const vb = d5 * d2 - d1 * d6;
  if (vb <= 0 && d2 >= 0 && d6 <= 0) {
    const t = d2 / (d2 - d6);
    return [1 - t, 0, t];
  }
  const va = d3 * d6 - d5 * d4;
  if (va <= 0 && d4 - d3 >= 0 && d5 - d6 >= 0) {
    const t = (d4 - d3) / (d4 - d3 + (d5 - d6));
    return [0, 1 - t, t];
  }
  const denom = va + vb + vc;
  if (!(Math.abs(denom) > 1e-20)) {
    // Degenerate triangle: fall back to the nearest corner.
    const ds = [dist(p, a), dist(p, b), dist(p, c)],
      i = ds.indexOf(Math.min(...ds));
    return [Number(i === 0), Number(i === 1), Number(i === 2)];
  }
  const v = vb / denom,
    t = vc / denom;
  return [1 - v - t, v, t];
}
const at = (bary: Vec3, a: Vec3, b: Vec3, c: Vec3): Vec3 =>
  [0, 1, 2].map((k) => bary[0] * a[k]! + bary[1] * b[k]! + bary[2] * c[k]!) as Vec3;

// ---------------------------------------------------------------------------

export interface MirrorOptions extends LockOptions {
  axis?: 0 | 1 | 2;
  center?: number;
  /** '+to-' copies the positive side onto the negative side; 'both' swaps sides. */
  direction?: '+to-' | '-to+' | 'both';
  tolerance?: number;
  selection?: ReadonlySet<number>;
}
export interface MatchResult {
  weights: VertexWeights;
  /** Target vertices that found no counterpart and were left unchanged/empty. */
  unmatched: number[];
}
/** Mirror: copies weights from each vertex's mirror counterpart, flipping L/R joint names. */
export function mirrorWeights(
  w: VertexWeights,
  positions: readonly Vec3[],
  {
    axis = 0,
    center = 0,
    direction = '+to-',
    tolerance = 1e-3,
    locked,
    selection,
  }: MirrorOptions = {},
): MatchResult {
  const grid = pointGrid(positions),
    unmatched: number[] = [];
  const weights = w.map((v, i) => {
    const p = positions[i];
    if (!p || (selection && !selection.has(i))) return copy(v);
    const side = p[axis] - center;
    if ((direction === '+to-' && side >= -tolerance) || (direction === '-to+' && side <= tolerance))
      return copy(v);
    const q = [...p] as Vec3;
    q[axis] = 2 * center - p[axis];
    const match = nearest(grid, q, (j) => dist(q, positions[j]!), tolerance);
    if (!match) {
      unmatched.push(i);
      return copy(v);
    }
    const source = (w[match.index] ?? []).map((s) => ({
      joint: mirrorName(s.joint),
      weight: s.weight,
    }));
    return finalize(v, source, { locked });
  });
  return { weights, unmatched };
}

export interface TransferSource {
  positions: readonly Vec3[];
  indices: readonly number[];
  weights: VertexWeights;
}
export interface TransferOptions {
  mode?: 'nearestVertex' | 'nearestFace';
  /** Target vertices farther than this from the source get no weights. */
  maxDistance?: number;
}
/** Transfer Weights: samples another mesh's weights at each target position. */
export function transferWeights(
  targetPositions: readonly Vec3[],
  source: TransferSource,
  { mode = 'nearestFace', maxDistance = Infinity }: TransferOptions = {},
): MatchResult {
  const unmatched: number[] = [];
  const triangles: [number, number, number][] = [];
  for (let t = 0; t + 2 < source.indices.length; t += 3) {
    const tri: [number, number, number] = [
      source.indices[t]!,
      source.indices[t + 1]!,
      source.indices[t + 2]!,
    ];
    if (tri.every((i) => source.positions[i])) triangles.push(tri);
  }
  const faces = mode === 'nearestFace' && triangles.length > 0;
  const grid = faces
    ? makeGrid(
        triangles.map((tri) => {
          const ps = tri.map((i) => source.positions[i]!);
          return [
            [0, 1, 2].map((k) => Math.min(...ps.map((p) => p[k]!))) as Vec3,
            [0, 1, 2].map((k) => Math.max(...ps.map((p) => p[k]!))) as Vec3,
          ];
        }),
      )
    : pointGrid(source.positions);
  const corners = (t: number) =>
    triangles[t]!.map((i) => source.positions[i]!) as [Vec3, Vec3, Vec3];
  const weights = targetPositions.map((p, i) => {
    if (faces) {
      const match = nearest(
        grid,
        p,
        (t) => {
          const [a, b, c] = corners(t);
          return dist(p, at(closestOnTriangle(p, a, b, c), a, b, c));
        },
        maxDistance,
      );
      if (!match) {
        unmatched.push(i);
        return [];
      }
      const [a, b, c] = corners(match.index),
        bary = closestOnTriangle(p, a, b, c);
      const mixed = triangles[match.index]!.flatMap((v, k) =>
        (source.weights[v] ?? []).map((s) => ({ joint: s.joint, weight: s.weight * bary[k]! })),
      );
      return finalize([], mixed, { allowEmpty: true });
    }
    const match = nearest(grid, p, (j) => dist(p, source.positions[j]!), maxDistance);
    if (!match) {
      unmatched.push(i);
      return [];
    }
    return finalize([], source.weights[match.index] ?? [], { allowEmpty: true });
  });
  return { weights, unmatched };
}

/** Limit Total: keep the strongest `limit` influences (locked ones first). */
export function limitTotal(
  w: VertexWeights,
  limit = MAX_INFLUENCES,
  { locked }: LockOptions = {},
): VertexWeights {
  return w.map((v) => finalize(v, v, { locked, limit }));
}

export interface LevelsOptions extends LockOptions {
  offset?: number;
  gain?: number;
  /** Joints to adjust; others are rescaled to fill. Default all (then renormalized). */
  joints?: ReadonlySet<string>;
  selection?: ReadonlySet<number>;
}
/** Levels: w' = clamp(w * gain + offset, 0, 1) for the chosen joints. */
export function levels(
  w: VertexWeights,
  { offset = 0, gain = 1, joints, locked, selection }: LevelsOptions = {},
): VertexWeights {
  return w.map((v, i) => {
    if (selection && !selection.has(i)) return copy(v);
    const candidate = [...merge(v)].map(([joint, weight]) => ({
      joint,
      weight:
        (!joints || joints.has(joint)) && !locked?.has(joint)
          ? clamp01(weight * gain + offset)
          : weight,
    }));
    return finalize(v, candidate, { locked, pinned: joints });
  });
}

export interface CleanOptions extends LockOptions {
  threshold?: number;
  /** Keep a vertex's strongest influence rather than leaving it empty. */
  keepSingle?: boolean;
  selection?: ReadonlySet<number>;
}
/** Clean: removes influences below `threshold` (locked joints are kept). */
export function clean(
  w: VertexWeights,
  { threshold = 0.01, keepSingle = true, locked, selection }: CleanOptions = {},
): VertexWeights {
  return w.map((v, i) => {
    if (selection && !selection.has(i)) return copy(v);
    const merged = [...merge(v)].sort(byWeight);
    let kept = merged.filter(([j, x]) => locked?.has(j) || x >= threshold);
    if (!kept.length && keepSingle && merged[0]) kept = [merged[0]];
    const candidate = kept.map(([joint, weight]) => ({ joint, weight }));
    return finalize(v, candidate, { locked, allowEmpty: !keepSingle });
  });
}

function blend(current: number, value: number, mode: BlendMode, amount = 1): number {
  const next =
    mode === 'add'
      ? current + value * amount
      : mode === 'subtract'
        ? current - value * amount
        : current + (value - current) * amount;
  return clamp01(Number.isFinite(next) ? next : current);
}
/** Sets one joint's weight on a vertex, rescaling the other unlocked joints to fill. */
function setJoint(
  v: readonly Influence[],
  joint: string,
  value: number,
  locked?: Locks,
): Influence[] {
  if (locked?.has(joint)) return copy(v);
  const candidate = [...v.filter((i) => i.joint !== joint), { joint, weight: value }];
  return finalize(v, candidate, { locked, pinned: new Set([joint]) });
}

export interface AssignOptions extends LockOptions {
  mode?: BlendMode;
}
/** Assigns `value` to `joint` on the selected vertices (replace/add/subtract). */
export function assignWeight(
  w: VertexWeights,
  joint: string,
  value: number,
  selection: ReadonlySet<number>,
  { mode = 'replace', locked }: AssignOptions = {},
): VertexWeights {
  return w.map((v, i) =>
    selection.has(i) ? setJoint(v, joint, blend(weightOf(v, joint), value, mode), locked) : copy(v),
  );
}

export interface GradientOptions extends LockOptions {
  mode?: 'linear' | 'radial';
  amount?: number;
  blend?: BlendMode;
  falloff?: 'linear' | 'smooth';
  selection?: ReadonlySet<number>;
}
/** Gradient: weight 1 at `from` fading to 0 at `to` (along the line or radially). */
export function gradient(
  w: VertexWeights,
  positions: readonly Vec3[],
  joint: string,
  from: Vec3,
  to: Vec3,
  {
    mode = 'linear',
    amount = 1,
    blend: blendMode = 'replace',
    falloff = 'linear',
    selection,
    locked,
  }: GradientOptions = {},
): VertexWeights {
  const axis = sub(to, from),
    length2 = dot(axis, axis),
    length = Math.sqrt(length2);
  return w.map((v, i) => {
    const p = positions[i];
    if (!p || (selection && !selection.has(i)) || !(length > 0)) return copy(v);
    const t = clamp01(
      mode === 'radial' ? dist(p, from) / length : dot(sub(p, from), axis) / length2,
    );
    let g = 1 - t;
    if (falloff === 'smooth') g = g * g * (3 - 2 * g);
    return setJoint(v, joint, blend(weightOf(v, joint), g, blendMode, amount), locked);
  });
}

// ---------------------------------------------------------------------------
// Rig document glue.

/**
 * A node's per-primitive weights: the rig's explicit weights, else the
 * engine's bound weights with joint indices resolved against `rig.joints`
 * (the same derivation as the rig store's `explicitWeights`).
 */
export function nodeWeights(
  rig: RigDoc,
  geometry: RigGeometry,
  frame: RigFrame,
  node: string,
): Influence[][][] {
  const primitives: Influence[][][] = structuredClone(rig.weights?.[node] ?? []);
  geometry.meshes.forEach((mesh, mi) => {
    if (mesh.node !== node) return;
    const count = frame.positions[mi]?.length ?? mesh.weights.length;
    const derived = () =>
      Array.from({ length: count }, (_, vi) =>
        finalize(
          [],
          (mesh.weights[vi] ?? []).map((weight, slot) => ({
            weight,
            joint: rig.joints[mesh.joints[vi]?.[slot] ?? 0]?.name ?? '',
          })),
          { allowEmpty: true },
        ),
      );
    const current = primitives[mesh.primitive] ?? derived();
    while (current.length < count) current.push([]);
    primitives[mesh.primitive] = current;
  });
  for (let i = 0; i < primitives.length; i++) primitives[i] ??= [];
  return primitives;
}

export interface MeshContext {
  node: string;
  primitive: number;
  positions: Vec3[];
  indices: number[];
}
/** Runs a weight tool on one mesh and returns a cloned rig with the result stored. */
export function applyToRig(
  rig: RigDoc,
  geometry: RigGeometry,
  frame: RigFrame,
  meshIndex: number,
  fn: (weights: VertexWeights, mesh: MeshContext) => VertexWeights,
): RigDoc {
  const mesh = geometry.meshes[meshIndex];
  if (!mesh) return rig;
  const primitives = nodeWeights(rig, geometry, frame, mesh.node);
  const positions = frame.positions[meshIndex] ?? [];
  primitives[mesh.primitive] = fn(primitives[mesh.primitive] ?? [], {
    node: mesh.node,
    primitive: mesh.primitive,
    positions,
    indices: mesh.indices,
  });
  const next = cloneRig(rig);
  next.weights ??= {};
  next.weights[mesh.node] = primitives;
  if (next.rigidBindings) delete next.rigidBindings[mesh.node];
  return next;
}
